import { useState, useEffect, useCallback, useMemo } from 'react';
import { initial, alsText, schrittText } from '@/lib/text';
import { useNavigate } from 'react-router-dom';
import { leadsAPI } from '@/lib/api';
import { useSettings } from '@/contexts/SettingsContext';
import { Clock, GripVertical, User, Calendar, Tag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import StufenwechselDialog from '@/components/StufenwechselDialog';

const BEREICH_OPTIONS = [
  { key: '', label: 'Alle' },
  { key: 'kinder_jugend', label: 'Kinder & Jugend' },
  { key: 'erwachsene', label: 'Erwachsene' },
  { key: 'kampagnen', label: 'Kampagnen' },
  { key: 'events', label: 'Events' },
];

export default function PipelinePage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [bereichFilter, setBereichFilter] = useState('');
  const { settings, formatDate } = useSettings();
  const navigate = useNavigate();

  // Dynamic pipeline stages based on selected bereich
  const STAGES = useMemo(() => {
    if (bereichFilter && settings.pipelines?.[bereichFilter]) {
      return settings.pipelines[bereichFilter];
    }
    return settings.pipeline_stages || [];
  }, [settings, bereichFilter]);

  const fetchLeads = useCallback(async () => {
    try {
      // Altbestand (vor 01.09.2026/Importe) nur, solange er noch im Verkauf steckt
      const params = { limit: 300, altbestand: 'ohne' };
      if (bereichFilter) params.bereich = bereichFilter;
      const res = await leadsAPI.list(params);
      setLeads(res.data.leads);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [bereichFilter]);

  useEffect(() => { setLoading(true); fetchLeads(); }, [fetchLeads]);

  const handleDragStart = (e, leadId) => {
    setDraggedId(leadId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!draggedId) return;

    const lead = leads.find(l => l.id === draggedId);
    if (!lead || lead.stage === newStage) { setDraggedId(null); return; }

    setLeads(prev => prev.map(l => l.id === draggedId ? { ...l, stage: newStage } : l));
    setDraggedId(null);

    // R2 (22.09.2026): verloren braucht einen Grund, andere Stufen einen
    // naechsten Schritt — beides fragt der StufenwechselDialog ab, statt
    // window.prompt bzw. eines nackten 422.
    if (newStage === 'verloren') { setStufenDialog({ leadId: draggedId, stage: newStage }); return; }
    try {
      await leadsAPI.updateStage(draggedId, newStage, {});
      const stageLabel = STAGES.find(s => s.key === newStage)?.label || newStage;
      toast.success(`Verschoben: ${stageLabel}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && typeof detail === 'string' && /Schritt|R2/.test(detail)) {
        setStufenDialog({ leadId: draggedId, stage: newStage, hinweis: detail });
        return;
      }
      fetchLeads();
      toast.error(detail || 'Verschieben fehlgeschlagen');
    }
  };

  const [stufenDialog, setStufenDialog] = useState(null);
  const stufenDialogBestaetigen = async (extra) => {
    try {
      await leadsAPI.updateStage(stufenDialog.leadId, stufenDialog.stage, extra);
      toast.success(`Verschoben: ${STAGES.find(s => s.key === stufenDialog.stage)?.label || stufenDialog.stage}`);
      setStufenDialog(null);
      fetchLeads();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Verschieben fehlgeschlagen'); }
  };

  const groupedLeads = STAGES.reduce((acc, stage) => {
    acc[stage.key] = leads.filter(l => l.stage === stage.key);
    return acc;
  }, {});

  const filteredTotal = leads.length;

  if (loading) {
    return (
      <div className="space-y-4 fade-in">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="flex gap-4 overflow-x-auto">
          {[...Array(5)].map((_, i) => <div key={`skeleton-${i}`} className="min-w-[280px] h-96 bg-muted animate-pulse rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-5 fade-in" data-testid="pipeline-page">
      <StufenwechselDialog offen={!!stufenDialog} stufeLabel={stufenDialog ? (STAGES.find(s => s.key === stufenDialog.stage)?.label || stufenDialog.stage) : ''} verloren={stufenDialog?.stage === 'verloren'} hinweis={stufenDialog?.hinweis} onBestaetigen={stufenDialogBestaetigen} onAbbrechen={() => { setStufenDialog(null); fetchLeads(); }} />
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Pipeline</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">{filteredTotal} Leads in {STAGES.length} Stufen</p>
        </div>
        {/* Bereich Switcher */}
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1" data-testid="pipeline-bereich-filter">
          {BEREICH_OPTIONS.map(opt => (
            <button key={opt.key} onClick={() => setBereichFilter(opt.key)} data-testid={`pipeline-bereich-${opt.key || 'alle'}`}
              className={`px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-all whitespace-nowrap ${
                bereichFilter === opt.key ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" style={{ minHeight: 'calc(100vh - 220px)' }}>
        {STAGES.map((stage) => {
          const stageLeads = groupedLeads[stage.key] || [];
          const isDropTarget = dragOverStage === stage.key;

          return (
            <div
              key={stage.key}
              className={`min-w-[240px] sm:min-w-[280px] w-[240px] sm:w-[280px] flex-shrink-0 rounded-lg border transition-colors ${
                isDropTarget ? 'kanban-drop-target' : 'border-border/40 bg-muted/20'
              }`}
              onDragOver={(e) => handleDragOver(e, stage.key)}
              onDragLeave={() => setDragOverStage(null)}
              onDrop={(e) => handleDrop(e, stage.key)}
              data-testid={`pipeline-stage-${stage.key}`}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: stage.color }} />
                  <span className="text-sm font-semibold">{stage.label}</span>
                </div>
                <span className="text-xs bg-muted px-2 py-0.5 rounded-full font-medium text-muted-foreground">
                  {stageLeads.length}
                </span>
              </div>

              <div className="p-2 space-y-2 min-h-[100px] max-h-[calc(100vh-280px)] overflow-y-auto">
                {stageLeads.map((lead) => (
                  <div
                    key={lead.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, lead.id)}
                    onDragEnd={() => setDraggedId(null)}
                    onClick={() => navigate(`/leads/${lead.id}`, { state: { from: '/pipeline', leadIds: leads.map(l => l.id) } })}
                    className={`bg-background border border-border/60 rounded-md p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-all group ${
                      draggedId === lead.id ? 'opacity-50 rotate-1' : ''
                    }`}
                    data-testid={`pipeline-card-${lead.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                          {initial(lead.name)}
                        </div>
                        <div>
                          <p className="text-sm font-medium leading-tight flex items-center gap-1.5">
                            {lead.ampel && (
                              <span data-testid={`ampel-${lead.id}`} title={`Einschätzung: ${lead.ampel}`}
                                    className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${{ gruen: 'bg-green-500', gelb: 'bg-amber-400', rot: 'bg-red-500' }[lead.ampel] || 'bg-slate-400'}`} />
                            )}
                            {alsText(lead.name)}
                          </p>
                          <p className="text-[10px] text-muted-foreground capitalize">{alsText(lead.source).replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                      <GripVertical className="w-3.5 h-3.5 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    <div className="mt-2 space-y-1">
                      {lead.naechster_schritt && (
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          <Tag className="w-3 h-3" />
                          <span className="truncate">{schrittText(lead.naechster_schritt)}</span>
                        </div>
                      )}
                      {lead.termin_status && (
                        <div className="flex items-center gap-1.5 text-[10px]">
                          <Calendar className="w-3 h-3" />
                          <span className={`font-medium ${lead.termin_status === 'no_show' ? 'text-red-500' : lead.termin_status === 'besucht' ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {lead.termin_status === 'geplant' ? 'Probetraining geplant' : lead.termin_status === 'besucht' ? 'Besucht' : lead.termin_status === 'no_show' ? 'No-Show' : 'Abgesagt'}
                          </span>
                        </div>
                      )}
                      {lead.next_follow_up && (
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>{formatDate(lead.next_follow_up)}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-2 pt-2 border-t border-border/30 flex items-center justify-between">
                      {lead.assigned_to_name ? (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1"><User className="w-2.5 h-2.5" />{lead.assigned_to_name}</span>
                      ) : <span />}
                      {lead.bereich && (
                        <Badge variant="secondary" className="text-[8px] h-4 px-1.5">
                          {lead.bereich === 'kinder_jugend' ? 'K&J' : lead.bereich === 'erwachsene' ? 'Erw' : lead.bereich === 'kampagnen' ? 'Kamp' : 'Event'}
                        </Badge>
                      )}
                      {lead.no_show && <Badge variant="destructive" className="text-[8px] h-4 px-1.5">No-Show</Badge>}
                    </div>
                  </div>
                ))}
                {stageLeads.length === 0 && (
                  <div className="text-center py-8 text-xs text-muted-foreground">
                    Leads hierher ziehen
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
